import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth, formatApiError } from "../lib/auth";
import { fmtMoney } from "../config";
import Seo from "../components/Seo";

export default function MyAccount() {
  const { user, logout, updateProfile } = useAuth();
  const [deals, setDeals] = useState([]);
  const [favs, setFavs] = useState([]);
  const [busy, setBusy] = useState(true);
  const [profile, setProfile] = useState({ name: "", phone: "", address: "" });
  const [profileMsg, setProfileMsg] = useState("");

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", phone: user.phone || "", address: user.address || "" });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [{ data: d }, { data: f }] = await Promise.all([
          api.get("/deals"), api.get("/favorites"),
        ]);
        setDeals(d.items || []);
        setFavs(f.items || []);
      } finally { setBusy(false); }
    })();
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg("");
    try {
      await updateProfile(profile);
      setProfileMsg("Saved.");
      setTimeout(() => setProfileMsg(""), 2000);
    } catch (ex) {
      setProfileMsg(formatApiError(ex));
    }
  };

  const removeFav = async (slug) => {
    await api.delete(`/favorites/${encodeURIComponent(slug)}`);
    setFavs((arr) => arr.filter((x) => x.vehicle_slug !== slug));
  };

  const deleteDeal = async (id) => {
    if (!window.confirm("Delete this saved deal?")) return;
    await api.delete(`/deals/${id}`);
    setDeals((arr) => arr.filter((x) => x.id !== id));
  };

  if (user === null) return <div className="p-12 text-center text-[var(--ssc-ink-2)]">Loading…</div>;
  if (!user) return (
    <main id="main-content" className="py-16 text-center">
      <h1 className="text-2xl font-black text-[var(--ssc-navy)]">Please sign in</h1>
      <Link to="/login" className="ssc-btn ssc-btn-primary mt-4 inline-block">Sign In</Link>
    </main>
  );

  return (
    <main id="main-content" className="bg-[var(--ssc-paper)] min-h-[70vh] py-10">
      <Seo title="My Account | Scott Sells Chevys" description="Manage your saved deals, trade estimates, and favorite vehicles." />
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-[var(--ssc-blue)]">Customer Portal</div>
            <h1 className="text-3xl font-black text-[var(--ssc-navy)]" data-testid="account-heading">
              Hi {user.name?.split(" ")[0] || "there"} 👋
            </h1>
            <p className="text-[var(--ssc-ink-2)] text-sm">Your saved deals, trade estimates, and favorites — all in one place.</p>
          </div>
          <button onClick={logout} className="ssc-btn ssc-btn-secondary text-sm" data-testid="account-logout">Sign out</button>
        </div>

        {/* Saved Deals */}
        <section className="ssc-card mb-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-xl font-black text-[var(--ssc-navy)]">Saved Deals</h2>
            <span className="text-xs text-[var(--ssc-ink-2)]" data-testid="deal-count">{deals.length} saved</span>
          </div>
          <p className="text-xs text-[var(--ssc-ink-2)] mt-1">Share your deal code with Scott — he can pull up every detail instantly.</p>
          {busy ? (
            <div className="py-6 text-[var(--ssc-ink-2)] text-sm">Loading…</div>
          ) : deals.length === 0 ? (
            <div className="py-8 text-center text-[var(--ssc-ink-2)] text-sm" data-testid="deal-empty">
              No saved deals yet. <Link className="text-[var(--ssc-blue)] font-semibold" to="/value-my-trade">Try the trade tool →</Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="deal-list">
              {deals.map((d) => (
                <div key={d.id} className="border border-[var(--ssc-line)] rounded-xl p-4 bg-white">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="text-[10px] font-bold tracking-widest uppercase text-[var(--ssc-blue)]">{d.deal_type}</div>
                      <div className="font-black text-[var(--ssc-ink)] mt-0.5">{d.title}</div>
                    </div>
                    <span className="font-mono font-extrabold text-sm bg-[var(--ssc-navy)] text-white px-2 py-1 rounded-md" data-testid={`deal-code-${d.code}`}>{d.code}</span>
                  </div>
                  {d.estimate?.dealer_trade_high && (
                    <div className="mt-3 text-sm">
                      <span className="text-[var(--ssc-ink-2)]">Trade range: </span>
                      <span className="font-bold text-[var(--ssc-blue)]">
                        {fmtMoney(d.estimate.dealer_trade_low)} – {fmtMoney(d.estimate.dealer_trade_high)}
                      </span>
                    </div>
                  )}
                  {d.payment?.monthly && (
                    <div className="mt-3 text-sm">
                      <span className="text-[var(--ssc-ink-2)]">Monthly: </span>
                      <span className="font-bold text-[var(--ssc-blue)]">{fmtMoney(d.payment.monthly)}/mo</span>
                      <span className="text-[var(--ssc-ink-2)]"> for {d.payment.term} mo</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center mt-3 pt-3 border-t border-[var(--ssc-line)]">
                    <span className="text-xs text-[var(--ssc-ink-2)]">
                      {new Date(d.created_at).toLocaleDateString()}
                    </span>
                    <button onClick={() => deleteDeal(d.id)} className="text-xs text-red-600 hover:underline" data-testid={`deal-delete-${d.code}`}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Favorites */}
        <section className="ssc-card mb-6">
          <h2 className="text-xl font-black text-[var(--ssc-navy)]">Favorite Vehicles</h2>
          {favs.length === 0 ? (
            <div className="py-6 text-center text-[var(--ssc-ink-2)] text-sm" data-testid="fav-empty">
              No favorites yet. <Link className="text-[var(--ssc-blue)] font-semibold" to="/used-vehicles">Browse inventory →</Link>
            </div>
          ) : (
            <ul className="mt-3 divide-y divide-[var(--ssc-line)]" data-testid="fav-list">
              {favs.map((f) => (
                <li key={f.vehicle_slug} className="py-3 flex justify-between items-center gap-3">
                  <Link to={`/vehicle/${f.vehicle_slug}`} className="font-semibold text-[var(--ssc-ink)] hover:text-[var(--ssc-blue)]">
                    {f.vehicle_title || f.vehicle_slug}
                  </Link>
                  <button onClick={() => removeFav(f.vehicle_slug)} className="text-xs text-red-600 hover:underline">Remove</button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Profile */}
        <section className="ssc-card">
          <h2 className="text-xl font-black text-[var(--ssc-navy)]">Your Info</h2>
          <form onSubmit={saveProfile} className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="profile-form">
            <Field label="Name"><input className="ssc-input" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name" /></Field>
            <Field label="Phone"><input className="ssc-input" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="profile-phone" /></Field>
            <Field label="Address" className="sm:col-span-2"><input className="ssc-input" value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} data-testid="profile-address" /></Field>
            <div className="sm:col-span-2 flex items-center justify-between">
              <span className="text-sm text-emerald-700">{profileMsg}</span>
              <button className="ssc-btn ssc-btn-primary" data-testid="profile-save">Save Changes</button>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="ssc-label">{label}</label>
    {children}
  </div>
);
